import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { notificationService } from '../services/notificationService';
import { useAuth } from '../auth/AuthContext';
import { orderService } from '../services/apiService';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [permission, setPermission] = useState(Notification.permission || 'default');
  const [isEnabled, setIsEnabled] = useState(false);
  const [showPermissionPrompt, setShowPermissionPrompt] = useState(false);

  // Check permission status
  useEffect(() => {
    if (notificationService.isSupported) {
      const currentPermission = Notification.permission;
      setPermission(currentPermission);
      setIsEnabled(currentPermission === 'granted');
      
      // Show prompt if user is authenticated and permission is default
      if (isAuthenticated && currentPermission === 'default' && !localStorage.getItem('notificationPromptShown')) {
        setTimeout(() => {
          setShowPermissionPrompt(true);
        }, 3000); // Show after 3 seconds
      }
    }
  }, [isAuthenticated]);

  // Request permission
  const requestPermission = useCallback(async () => {
    const granted = await notificationService.requestPermission();
    if (granted) {
      setPermission('granted');
      setIsEnabled(true);
      setShowPermissionPrompt(false);
      localStorage.setItem('notificationPromptShown', 'true');
      localStorage.setItem('notificationsEnabled', 'true');

      // Best-effort: create/update backend push subscription for this user
      try {
        const sub = await notificationService.getOrCreatePushSubscription(process.env.REACT_APP_VAPID_PUBLIC_KEY);
        if (sub) {
          await orderService.subscribeToNotifications(sub);
        }
      } catch (e) {
        console.warn('Failed to register push subscription with backend:', e);
      }
    } else {
      setPermission('denied');
      setIsEnabled(false);
      localStorage.setItem('notificationPromptShown', 'true');
    }
    return granted;
  }, []);

  // Dismiss permission prompt
  const dismissPermissionPrompt = useCallback(() => {
    setShowPermissionPrompt(false);
    localStorage.setItem('notificationPromptShown', 'true');
  }, []);

  // Show notification
  const showNotification = useCallback(async (options) => {
    if (!isEnabled) return;
    return await notificationService.showNotification(options);
  }, [isEnabled]);

  // Order status notification
  const notifyOrderStatus = useCallback(async (orderId, status, orderData) => {
    if (!isEnabled) return;
    return await notificationService.notifyOrderStatus(orderId, status, orderData);
  }, [isEnabled]);

  // Promotion notification
  const notifyPromotion = useCallback(async (title, message, promotionId, url) => {
    if (!isEnabled) return;
    return await notificationService.notifyPromotion(title, message, promotionId, url);
  }, [isEnabled]);

  // Payment notification
  const notifyPayment = useCallback(async (orderId, status, amount) => {
    if (!isEnabled) return;
    return await notificationService.notifyPayment(orderId, status, amount);
  }, [isEnabled]);

  // Shipping notification
  const notifyShipping = useCallback(async (orderId, status, trackingNumber) => {
    if (!isEnabled) return;
    return await notificationService.notifyShipping(orderId, status, trackingNumber);
  }, [isEnabled]);

  // Return notification
  const notifyReturn = useCallback(async (orderId, status) => {
    if (!isEnabled) return;
    return await notificationService.notifyReturn(orderId, status);
  }, [isEnabled]);

  // Clear all notifications
  const clearAll = useCallback(async () => {
    await notificationService.clearAllNotifications();
  }, []);

  const value = {
    permission,
    isEnabled,
    isSupported: notificationService.isSupported,
    showPermissionPrompt,
    requestPermission,
    dismissPermissionPrompt,
    showNotification,
    notifyOrderStatus,
    notifyPromotion,
    notifyPayment,
    notifyShipping,
    notifyReturn,
    clearAll
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

